import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Flame, Radio, Ticket } from "lucide-react";
import type { HomeViewProps } from "./types";
import { PackSpotlight } from "./PackSpotlight";
import { LivePullMarquee } from "./LivePullMarquee";
import { HeroCarousel } from "../HeroCarousel";
import { ProductCard, ProductImage } from "../ProductCard";
import { PackCard } from "../storefront/PackCard";
import { RaffleCard } from "../storefront/RaffleCard";
import { AuctionCard } from "../storefront/AuctionCard";
import { LeaderboardTeaser } from "../storefront/LeaderboardTeaser";
import { SectionHeader } from "../storefront/SectionHeader";
import { Countdown } from "../Countdown";
import { Reveal } from "../fx/Reveal";
import { rm } from "../../lib/format";
import { useAuth } from "../../stores/auth";

/**
 * Desktop home — an editorial bento layout. Nothing scrolls horizontally here;
 * the extra width is spent on parallel grids and a persistent "live now" rail.
 */
export function HomeDesktop({ raffles, packs, products, auctions, leaderboard }: HomeViewProps) {
  const { moduleEnabled } = useAuth();
  const openRaffles = raffles.filter((r) => r.status === "OPEN");
  const heroRaffle = openRaffles[0];
  const heroPack = packs[0];
  const liveAuctions = auctions.filter((a) => a.status === "LIVE");

  return (
    <div className="space-y-12 px-6 pb-14 pt-2">
      {/* ---------- hero band ---------- */}
      <section className="grid grid-cols-[minmax(0,1fr)_340px] items-start gap-6">
        <div className="min-w-0 self-start">
          <HeroCarousel />
        </div>

        <aside className="flex flex-col gap-3 self-stretch">
          <div className="glass-panel rounded-3xl p-4">
            <p className="flex items-center gap-1.5 font-display text-[10px] font-bold uppercase tracking-[0.28em] text-brand-pink">
              <Radio size={11} /> Live now
            </p>

            {heroRaffle ? (
              <Link to={`/raffles/${heroRaffle.id}`} className="mt-3 block group">
                <div className="relative overflow-hidden rounded-2xl">
                  <ProductImage
                    src={heroRaffle.product.images[0] ?? null}
                    alt=""
                    className="aspect-[16/10] w-full transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-black/35" />
                  <span className="absolute left-2.5 top-2.5 z-10 inline-flex items-center gap-1 rounded-full bg-black/55 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-emerald-300 backdrop-blur-sm">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 pulse-ring" /> Open
                  </span>
                </div>
                <p className="mt-2.5 line-clamp-1 text-xs font-semibold">{heroRaffle.title}</p>
                <div className="mt-1.5 flex items-center justify-between">
                  <span className="text-[10px] text-white/45">{heroRaffle.ticketCount} tickets in</span>
                  <Countdown target={heroRaffle.entryClosesAt} compact neon />
                </div>
                <p className="mt-1 text-[10px] text-white/45">
                  Winner buys at <span className="text-brand-pink">{rm(heroRaffle.priceForWinner)}</span>
                </p>
              </Link>
            ) : (
              <div className="mt-3 flex flex-col items-center gap-2 py-6">
                <img src="/art/mascot.svg" alt="" className="h-16 w-16 opacity-45" />
                <p className="text-center text-[11px] text-white/40">No raffle running — new lobbies open daily.</p>
              </div>
            )}
          </div>

          <LivePullMarquee />

          {liveAuctions.length > 0 && (
            <div className="glass-panel rounded-3xl p-4">
              <p className="mb-3 flex items-center gap-1.5 font-display text-[10px] font-bold uppercase tracking-[0.28em] text-brand-pink">
                <Flame size={11} /> Hot lots
              </p>
              <div className="space-y-2">
                {liveAuctions.slice(0, 3).map((auction) => (
                  <AuctionCard key={auction.id} auction={auction} />
                ))}
              </div>
            </div>
          )}
        </aside>
      </section>

      {/* ---------- packs ---------- */}
      {moduleEnabled("packs") && packs.length > 0 && (
        <Reveal as="section">
          <SectionHeader title="Mystery packs" hint="pick · rip · flip" to="/packs" />
          <div className="grid grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)] gap-5">
            {heroPack && <PackSpotlight pack={heroPack} />}
            <div className="grid grid-cols-2 gap-4">
              {packs.slice(1, 5).map((pack) => (
                <PackCard key={pack.id} pack={pack} />
              ))}
            </div>
          </div>
        </Reveal>
      )}

      {/* ---------- raffles ---------- */}
      {moduleEnabled("raffles") && openRaffles.length > 0 && (
        <Reveal as="section">
          <SectionHeader title="Raffles" hint="win the right to buy" to="/raffles" />
          <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)] gap-5">
            {heroRaffle && <RaffleCard raffle={heroRaffle} variant="hero" />}
            <div className="space-y-2.5">
              {openRaffles.slice(1, 6).map((raffle) => (
                <RaffleCard key={raffle.id} raffle={raffle} variant="compact" />
              ))}
              {openRaffles.length <= 1 && (
                <Link
                  to="/raffles"
                  className="flex h-full items-center justify-center gap-2 rounded-2xl border border-dashed border-brand-pink/30 p-6 text-xs font-semibold text-brand-pink transition-colors hover:bg-brand-pink/5"
                >
                  <Ticket size={14} /> See every lobby
                </Link>
              )}
            </div>
          </div>
        </Reveal>
      )}

      {/* ---------- shop ---------- */}
      {moduleEnabled("ecommerce") && products.length > 0 && (
        <Reveal as="section">
          <SectionHeader title="Featured products" hint="shop favorites" to="/shop" />
          <div className="grid grid-cols-5 gap-4">
            {products.slice(0, 10).map((product, i) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: (i % 5) * 0.05, duration: 0.4 }}
              >
                <ProductCard product={product} />
              </motion.div>
            ))}
          </div>
        </Reveal>
      )}

      {/* ---------- community ---------- */}
      <Reveal as="section">
        <div
          className={
            leaderboard.length > 0
              ? "grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-5"
              : "max-w-md"
          }
        >
          {leaderboard.length > 0 && <LeaderboardTeaser entries={leaderboard} />}

          <div className="relative overflow-hidden rounded-2xl neon-ring bg-gradient-to-br from-brand-blue/20 via-surface to-brand-pink/20 p-6">
            <img
              src="/art/ticket.svg"
              alt=""
              className="absolute -right-6 -top-4 w-40 rotate-12 opacity-20"
            />
            <p className="font-display text-sm font-bold">Track your order</p>
            <p className="mt-1.5 max-w-xs text-[11px] leading-relaxed text-white/50">
              Enter an order number any time — no sign-in needed. Parcel status, tracking and delivery proof in one
              place.
            </p>
            <Link
              to="/track"
              className="mt-4 inline-flex items-center gap-1.5 rounded-xl bg-white/10 px-4 py-2.5 text-xs font-semibold transition-colors hover:bg-white/20"
            >
              Track a parcel <ArrowRight size={13} />
            </Link>
          </div>
        </div>
      </Reveal>
    </div>
  );
}
